import { useAuth } from "@clerk/clerk-react";
import { Box, IconButton, Paper, TextField, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import Markdown from "components/Markdown";
import TypingLoader from "features/Home/Sidebar/ChatHelper/TypingLoader";
import { fetcherAuth } from "utils/api";

import UpgradeDialog from "./UpgradeDialog";

interface Message {
  role?: string;
  content: string;
  tool_call_id?: string;
  name?: string;
  type?: string;
}

const _INITIAL_MESSAGES = [
  {
    role: "system",
    content: `You are an assistant helping me with creating an email template to write to brands. You are a social media marketing expert, 
and are young, so talk like a young UGC creator.

The first step as the assistant is to help me with crafting my email template. Here are the questions you should ask me (ideally one question at a time):
- Are you a UGC creator or influencer (or both)?
- What is your location?
- If I respond saying influencer, ask me for my social media following and platform of choice?
- Ask me for my portfolio site

Use the "get_portfolio_site" tool to read my portfolio site. Using the information below, write an email template and ask me to approve it or request edits.

Here is an example template:
---
Hi {First Name},

Hope your week is going great! I recently worked with Credo Beauty, Mara, and Dr. Loretta to put together some UGC content for them and {Brand Name} popped up as a brand I'd love to work with next!

Quick intro-- I'm Jaclyn a NYC-based founder of a YC-backed tech startup. As a side hobby, I create content for brands I love (you could say it's like a stress-reliever from being a business owner)!

I'd love to see if {Brand Name} is focusing on UGC content now or in the near future? Have a ton of ideas I'd love to bring to life for ya'll!

Is this something you/ someone on your team might be interested in chatting further about? Happy to share more about what I'm thinking, share my portfolio or hop on a call!

Looking forward to hearing from you,

Jaclyn
Portfolio (hyperlinked)
---
You should use variables like "First Name" and "Brand Name" in the template so it can be used for multiple brands.

After I approve it and I don't want to make any more edits, please ask me if I want you to send emails on my behalf (plans starting at $50 a month) or if I want to do it on my own (free version). You can ask it in this way:

"Next up—do you want our AI Virtual Assistant to send hundreds of emails each month for you ($60/month), or do you want to pitch yourself (free)?"

If I am interested in you sending emails on my behalf, call the "show_pricing_plans" function. If I choose to do it on my own, then ask me if I want to go the dashboard or send some emails right now. For sending emails, you can use the "get_brands" function to get brands. After getting a few brands, ask me which brand I would like to email (show some information about the brand, the most important info is if the brand works with creators or not and the category of the brand). You can use the "send_brand_email" function only after I confirm that the brand looks good to send and the template.
`,
  },
];

const ChatForm = () => {
  const [messages, setMessages] = useState<Message[]>(_INITIAL_MESSAGES);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false);
  const [upgradeDialog, setUpgradeDialog] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const chatEndRef = useRef<HTMLDivElement>(null);
  const { getToken } = useAuth();
  const { currentOrg, currentUser } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (messages.length === 1) {
      displayAssistantMessage(
        `Hey ${currentUser?.name}! 👋 Hope you're having a great day. I'm your Bento Virtual Assistant here to help you get those brand collabs rolling.\n\nBefore we start sending emails on your behalf, we'll need to create your go-to email template — don't worry, I'll walk you through it step by step. Let's make something that sounds like *you* and gets brands excited to work together.\n\nSo let's get started, are you a UGC creator, social media influencer or both?`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, typing]);

  const displayAssistantMessage = (text: string) => {
    setTyping(true);

    const newMessage: Message = { role: "assistant", content: "" };
    setMessages((prev) => [...prev, newMessage]);

    let index = 0;

    const interval = setInterval(() => {
      setMessages((prev) => {
        const updated = [...prev];
        const assistantMsg = { ...updated[updated.length - 1] };

        assistantMsg.content = text.slice(0, index + 1);
        updated[updated.length - 1] = assistantMsg;

        return updated;
      });

      index++;
      if (index >= text.length) {
        clearInterval(interval);
        setTyping(false);
      }
    }, 10);
  };

  const handleSubmit = async (updatedMessages: Message[]) => {
    if (!input.trim()) return;

    setLoading(true);

    try {
      const { messages } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/demo/chat`,
        "POST",
        {},
        {
          messages: updatedMessages,
        },
      );
      const reply = messages[messages.length - 1];
      if (reply.tool_calls) {
        setMessages(messages);
        const functionName = reply.tool_calls?.[0]?.function?.name;
        if (functionName === "go_to_dashboard") {
          navigate(`/${routes.home}`);
          setLoading(false);
        } else if (functionName === "show_pricing_plans") {
          setUpgradeDialog(true);
          setMessages((prev) => [
            ...prev,
            {
              role: "tool",
              type: "function_call_output",
              tool_call_id: reply.tool_calls?.[0]?.id,
              name: "show_pricing_plans",
              content: "purchase cancelled",
            },
          ]);
          setLoading(false);
        } else {
          setLoadingMessage(functionName);
          handleSubmit(messages);
        }
      } else {
        setMessages(messages.slice(0, -1));

        displayAssistantMessage(messages[messages.length - 1].content);
        setLoading(false);
      }
    } catch (error) {
      setErrorAlert(error);
      setLoading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" p={2}>
      <Box flexGrow={1} overflow="auto" mb={2}>
        {messages
          .filter((msg) => msg.role === "user" || msg.role === "assistant")
          .map((msg, i) => (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
              }}
              key={i}
            >
              <Paper
                elevation={0}
                sx={{
                  px: msg.role === "user" ? 2 : 0,
                  py: 0.75,
                  my: 1,
                  bgcolor: msg.role === "user" ? "primary.main" : "transparent",
                  whiteSpace: "pre-wrap",
                  borderRadius: "16px",
                }}
              >
                <Markdown
                  content={msg.content}
                  color={msg.role === "user" ? "white" : "inherit"}
                />
              </Paper>
            </Box>
          ))}
        {loading && (
          <Typography
            component="div"
            variant="body2"
            color="text.secondary"
            ml={1}
          >
            <Paper
              elevation={0}
              sx={{
                py: 0.75,
                my: 1,
                bgcolor: "transparent",
                whiteSpace: "pre-wrap",
              }}
            >
              <TypingLoader
                title={
                  loadingMessage === "get_portfolio_site"
                    ? "Reading your portfolio site"
                    : loadingMessage === "get_brands"
                      ? "Accessing our 100K+ brands database"
                      : loadingMessage === "send_brand_email"
                        ? "Drafting your email"
                        : ""
                }
              />
            </Paper>
          </Typography>
        )}
        <div ref={chatEndRef} />
      </Box>

      <Box
        component="form"
        onSubmit={(event: CustomEvent) => {
          event.preventDefault();
          setInput("");
          const userMessage: Message = { role: "user", content: input };
          const updatedMessages = [...messages, userMessage];
          setMessages(updatedMessages);
          setLoadingMessage("");
          handleSubmit(updatedMessages);
        }}
        display="flex"
        gap={1}
      >
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          fullWidth
          placeholder="Type your response"
          disabled={loading || typing}
        />
        <IconButton type="submit" disabled={loading || typing || !input.trim()}>
          <Box component="span" className="fa-solid fa-paper-plane-top" />
        </IconButton>
      </Box>
      {upgradeDialog && (
        <UpgradeDialog
          open={upgradeDialog}
          handleClose={() => {
            setUpgradeDialog(false);
          }}
        />
      )}
    </Box>
  );
};

export default ChatForm;
